/**
 * PrintManagerDialog.tsx
 * ──────────────────────
 * Cross-platform printer manager — fully themed to match MenuMaster POS
 * (light/dark glass-morphism, food-red accent, warm neutral palette).
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Bluetooth,
  BluetoothConnected,
  BluetoothDisabled,
  BluetoothSearching,
  Close,
  Computer,
  PhoneAndroid,
  Print,
  PrintDisabled,
  Refresh,
  ReceiptLong,
  Wifi,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useThemeModeContext } from '../hooks/useThemeMode';
import { food, shadows } from '../theme';
import bluetoothPrinter, { BillData, PrinterStatus } from '../services/BluetoothPrinterService';
import { printViaSystemDialog, generateReceiptHTML } from '../utils/receiptHtml';

// ── Types ─────────────────────────────────────────────────────────────────────
interface KnownDevice {
  id: string;
  name: string;
  lastSeen?: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  bill: BillData | null;
}

// ── Platform detection ────────────────────────────────────────────────────────
const isAndroid  = /android/i.test(navigator.userAgent);
const isIOS      = /iphone|ipad|ipod/i.test(navigator.userAgent);
const isCapacitor = !!(window as any).Capacitor;
const hasBluetooth = 'bluetooth' in navigator;
const STORAGE_KEY = 'menumaster.knownBtPrinters';

function loadKnown(): KnownDevice[] {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '[]'); } catch { return []; }
}
function saveKnown(d: KnownDevice[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(d));
}

// ── Component ─────────────────────────────────────────────────────────────────
const PrintManagerDialog: React.FC<Props> = ({ open, bill, onClose }) => {
  const { isDark } = useThemeModeContext();

  // ── Theme tokens (mirrors POS glassCard) ──────────────────────────────────
  const glass = {
    bg:     isDark ? food.glassDark          : food.glass,
    border: isDark ? food.glassDarkBorder    : food.glassBorder,
    surface: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.6)',
    divider: isDark ? 'rgba(255,255,255,0.07)' : 'rgba(200,185,170,0.25)',
    subtle:  isDark ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.5)',
  };
  const txt = {
    primary:   isDark ? food.snowPrimary   : food.inkDark,
    secondary: isDark ? food.snowSecondary : food.inkMid,
    muted:     isDark ? food.snowMuted     : food.inkLight,
  };
  const accent = food.red;
  const accentGlow = food.redGlow;
  const accentSoft = food.redSoft;

  // ── State ─────────────────────────────────────────────────────────────────
  const [tab, setTab] = useState(0);
  const [printerStatus, setPrinterStatus] = useState<PrinterStatus>({ connected: false });
  const [knownDevices, setKnownDevices] = useState<KnownDevice[]>([]);
  const [scanning, setScanning] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [feedback, setFeedback] = useState<{ msg: string; type: 'success' | 'error' | 'info' } | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  // Register BT status callback once
  useEffect(() => { bluetoothPrinter.setStatusCallback(setPrinterStatus); }, []);

  // Load known BT devices when dialog opens
  const refreshKnown = useCallback(async () => {
    const stored = loadKnown();
    if (hasBluetooth) {
      try {
        const granted: BluetoothDevice[] = await navigator.bluetooth.getDevices();
        const mapped: KnownDevice[] = granted.map((d) => ({
          id: d.id,
          name: d.name ?? 'Unknown Printer',
        }));
        const ids = new Set(mapped.map((d) => d.id));
        const merged = [...mapped, ...stored.filter((s) => !ids.has(s.id))];
        setKnownDevices(merged);
        saveKnown(merged);
        return;
      } catch { /* fall through */ }
    }
    setKnownDevices(stored);
  }, []);

  useEffect(() => {
    if (open) { refreshKnown(); setFeedback(null); }
  }, [open, refreshKnown]);

  // ── Handlers ─────────────────────────────────────────────────────────────
  const handleScan = async () => {
    if (!hasBluetooth) {
      setFeedback({ msg: 'Web Bluetooth not supported. Use Chrome or Edge.', type: 'error' });
      return;
    }
    setScanning(true);
    setFeedback({ msg: 'Opening Bluetooth scanner…', type: 'info' });
    const status = await bluetoothPrinter.connect();
    setScanning(false);
    if (status.connected && status.deviceName) {
      const dev: KnownDevice = { id: Date.now().toString(), name: status.deviceName, lastSeen: new Date().toISOString() };
      setKnownDevices((prev) => {
        const updated = [dev, ...prev.filter((d) => d.name !== status.deviceName)];
        saveKnown(updated);
        return updated;
      });
      setFeedback({ msg: `Connected to ${status.deviceName}`, type: 'success' });
    } else if (status.error && !status.error.includes('cancelled')) {
      setFeedback({ msg: status.error, type: 'error' });
    } else { setFeedback(null); }
  };

  const handleConnectKnown = async (device: KnownDevice) => {
    if (printerStatus.connected && printerStatus.deviceName === device.name) {
      bluetoothPrinter.disconnect();
      setFeedback({ msg: `Disconnected from ${device.name}`, type: 'info' });
      return;
    }
    setScanning(true);
    setFeedback({ msg: `Connecting to ${device.name}…`, type: 'info' });
    const status = await bluetoothPrinter.connect();
    setScanning(false);
    if (status.connected) setFeedback({ msg: `Connected to ${status.deviceName}`, type: 'success' });
    else if (status.error && !status.error.includes('cancelled')) setFeedback({ msg: status.error, type: 'error' });
    else setFeedback(null);
  };

  const handleForget = (id: string) => {
    setKnownDevices((prev) => { const u = prev.filter((d) => d.id !== id); saveKnown(u); return u; });
  };

  const handleBluetoothPrint = async () => {
    if (!bill || !printerStatus.connected) {
      setFeedback({ msg: 'Connect a Bluetooth printer first.', type: 'error' });
      return;
    }
    setPrinting(true);
    setFeedback({ msg: 'Sending to printer…', type: 'info' });
    try {
      await bluetoothPrinter.printBill(bill);
      setFeedback({ msg: '✓ Bill sent to printer!', type: 'success' });
    } catch (err: any) {
      setFeedback({ msg: `Print failed: ${err.message}`, type: 'error' });
    } finally { setPrinting(false); }
  };

  const handleTestPrint = async () => {
    if (!printerStatus.connected) {
      setFeedback({ msg: 'Connect a Bluetooth printer first.', type: 'error' });
      return;
    }
    setPrinting(true);
    setFeedback({ msg: 'Sending test receipt…', type: 'info' });
    try {
      await bluetoothPrinter.printTestReceipt();
      setFeedback({ msg: '✓ Test receipt sent. Check the printer.', type: 'success' });
    } catch (err: any) {
      setFeedback({ msg: `Test print failed: ${err.message}`, type: 'error' });
    } finally { setPrinting(false); }
  };

  const handleSystemPrint = () => {
    if (!bill) return;
    const ok = printViaSystemDialog(bill);
    setFeedback(ok
      ? { msg: 'Print dialog opened — select your printer.', type: 'success' }
      : { msg: 'Popup blocked. Allow popups for this site, then try again.', type: 'error' },
    );
  };

  // ── Shared style helpers ─────────────────────────────────────────────────
  const cardBox = {
    borderRadius: '14px',
    border: `1px solid ${glass.border}`,
    background: glass.subtle,
    backdropFilter: 'blur(12px)',
  };

  const accentCard = {
    borderRadius: '14px',
    border: `1px solid ${accentSoft}`,
    background: isDark ? 'rgba(224,42,42,0.08)' : 'rgba(224,42,42,0.05)',
  };

  const successCard = {
    borderRadius: '12px',
    border: '1px solid rgba(56,142,60,0.4)',
    background: 'rgba(56,142,60,0.08)',
  };

  const platformLabel = isAndroid || isCapacitor ? 'Android' : isIOS ? 'iOS' : 'Windows / PC';
  const platformIcon  = isAndroid || isCapacitor ? <PhoneAndroid fontSize="small" /> : <Computer fontSize="small" />;
  const BTIcon = printerStatus.connected ? BluetoothConnected : Bluetooth;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '24px',
          background: isDark ? 'rgba(28,24,22,0.96)' : 'rgba(255,255,255,0.97)',
          backdropFilter: 'blur(32px)',
          WebkitBackdropFilter: 'blur(32px)',
          border: `1px solid ${glass.border}`,
          boxShadow: isDark
            ? '0 24px 64px rgba(0,0,0,0.65)'
            : '0 24px 64px rgba(26,18,9,0.16)',
          overflow: 'hidden',
          color: txt.primary,
        },
      }}
    >
      {/* ── Title bar ────────────────────────────────────────────────────── */}
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          pb: 0,
          background: isDark
            ? `linear-gradient(90deg, ${accentSoft}, transparent)`
            : `linear-gradient(90deg, rgba(224,42,42,0.04), transparent)`,
          borderBottom: `1px solid ${glass.divider}`,
        }}
      >
        <Box
          sx={{
            width: 36, height: 36,
            borderRadius: '10px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: `linear-gradient(135deg, ${accent}, ${food.redLight})`,
            boxShadow: `0 4px 12px ${accentGlow}`,
            flexShrink: 0,
          }}
        >
          <Print sx={{ color: '#fff', fontSize: '1.1rem' }} />
        </Box>
        <Box flex={1}>
          <Typography fontWeight={800} fontSize="1rem" color={txt.primary}>
            Print Manager
          </Typography>
          <Typography variant="caption" color={txt.muted}>
            Select a printer and print your bill
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ color: txt.muted }}>
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {/* ── Tabs ───────────────────────────────────────────────────────── */}
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{
            px: 2,
            borderBottom: `1px solid ${glass.divider}`,
            '& .MuiTab-root': {
              color: txt.secondary,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.85rem',
            },
            '& .Mui-selected': { color: accent },
            '& .MuiTabs-indicator': { backgroundColor: accent, borderRadius: '9999px' },
          }}
        >
          <Tab id="tab-bluetooth" icon={<BTIcon fontSize="small" />} iconPosition="start" label="Bluetooth Printers" />
          <Tab id="tab-system" icon={platformIcon} iconPosition="start" label={`${platformLabel} System Print`} />
        </Tabs>

        {/* ── Feedback bar ───────────────────────────────────────────────── */}
        <AnimatePresence>
          {feedback && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}>
              <Alert severity={feedback.type} onClose={() => setFeedback(null)} sx={{ m: 2, mb: 0, borderRadius: '10px' }}>
                {feedback.msg}
              </Alert>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ──────────────────────────────────────────────────────────────── */}
        {/* TAB 0 — BLUETOOTH                                                */}
        {/* ──────────────────────────────────────────────────────────────── */}
        {tab === 0 && (
          <Box p={2}>
            {!hasBluetooth ? (
              <Alert severity="warning" sx={{ borderRadius: '10px' }}>
                Web Bluetooth is not available. Please use <strong>Chrome or Edge</strong> on Windows / Android.
              </Alert>
            ) : (
              <>
                {/* Connected banner */}
                {printerStatus.connected && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, mb: 2, ...successCard }}>
                    <BluetoothConnected sx={{ color: '#388E3C' }} />
                    <Box flex={1}>
                      <Typography fontSize="0.85rem" fontWeight={700} color="#388E3C">
                        {printerStatus.deviceName ?? 'Connected'}
                      </Typography>
                      <Typography variant="caption" color={txt.muted}>
                        Ready to print via ESC/POS (direct thermal)
                      </Typography>
                    </Box>
                    <Button
                      size="small"
                      variant="contained"
                      color="success"
                      startIcon={printing ? <CircularProgress size={14} color="inherit" /> : <Print fontSize="small" />}
                      onClick={handleBluetoothPrint}
                      disabled={printing || !bill?.items.length}
                      sx={{ borderRadius: '9999px', textTransform: 'none', fontWeight: 700, whiteSpace: 'nowrap' }}
                    >
                      {printing ? 'Printing…' : 'Print Bill'}
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={printing ? <CircularProgress size={14} /> : <ReceiptLong fontSize="small" />}
                      onClick={handleTestPrint}
                      disabled={printing}
                      sx={{ borderRadius: '9999px', textTransform: 'none', fontWeight: 700, whiteSpace: 'nowrap' }}
                    >
                      Test Print
                    </Button>
                  </Box>
                )}

                {/* Scan / Refresh row */}
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
                  <Typography
                    variant="caption"
                    sx={{ color: txt.muted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}
                  >
                    Available Printers
                  </Typography>
                  <Box display="flex" gap={0.5} alignItems="center">
                    <Tooltip title="Refresh known devices">
                      <IconButton size="small" onClick={refreshKnown} sx={{ color: txt.muted }}>
                        <Refresh fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Button
                      id="scan-bt-btn"
                      size="small"
                      variant="outlined"
                      color="primary"
                      startIcon={scanning ? <CircularProgress size={14} color="inherit" /> : <BluetoothSearching fontSize="small" />}
                      onClick={handleScan}
                      disabled={scanning}
                      sx={{ borderRadius: '9999px', textTransform: 'none', fontWeight: 600 }}
                    >
                      {scanning ? 'Scanning…' : 'Scan for Printer'}
                    </Button>
                  </Box>
                </Box>

                {/* Printer list */}
                {knownDevices.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 5, ...cardBox }}>
                    <PrintDisabled sx={{ fontSize: 48, color: txt.muted, mb: 1, opacity: 0.5 }} />
                    <Typography color={txt.secondary} fontSize="0.85rem" fontWeight={600}>
                      No printers found yet
                    </Typography>
                    <Typography color={txt.muted} variant="caption">
                      Turn on your printer and click <strong style={{ color: accent }}>Scan for Printer</strong>
                    </Typography>
                  </Box>
                ) : (
                  <List disablePadding>
                    {knownDevices.map((device, idx) => {
                      const isConn = printerStatus.connected && printerStatus.deviceName === device.name;
                      return (
                        <motion.div
                          key={device.id}
                          initial={{ opacity: 0, y: 6 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: idx * 0.04 }}
                        >
                          <ListItem
                            sx={{
                              borderRadius: '12px',
                              mb: 0.75,
                              border: `1px solid`,
                              borderColor: isConn ? 'rgba(56,142,60,0.45)' : glass.border,
                              background: isConn
                                ? 'rgba(56,142,60,0.07)'
                                : glass.surface,
                              backdropFilter: 'blur(8px)',
                              '&:hover': {
                                borderColor: accentSoft,
                                background: isDark ? accentSoft : 'rgba(224,42,42,0.04)',
                              },
                              transition: 'all 0.18s ease',
                            }}
                          >
                            <ListItemIcon sx={{ minWidth: 40 }}>
                              {isConn
                                ? <BluetoothConnected sx={{ color: '#388E3C' }} />
                                : <Bluetooth sx={{ color: txt.muted }} />
                              }
                            </ListItemIcon>
                            <ListItemText
                              primary={
                                <Typography fontWeight={700} fontSize="0.875rem" color={txt.primary}>
                                  {device.name}
                                </Typography>
                              }
                              secondary={
                                <Typography variant="caption" color={txt.muted}>
                                  {isConn
                                    ? 'Connected — ready to print'
                                    : device.lastSeen
                                    ? `Last used: ${new Date(device.lastSeen).toLocaleDateString('en-IN')}`
                                    : 'Previously paired'}
                                </Typography>
                              }
                            />
                            <ListItemSecondaryAction>
                              <Box display="flex" gap={0.5} alignItems="center">
                                {isConn ? (
                                  <Chip
                                    label="Disconnect"
                                    size="small"
                                    variant="outlined"
                                    color="error"
                                    icon={<BluetoothDisabled fontSize="small" />}
                                    onClick={() => handleConnectKnown(device)}
                                    sx={{ cursor: 'pointer', fontSize: '0.7rem' }}
                                  />
                                ) : (
                                  <Chip
                                    label="Connect"
                                    size="small"
                                    variant="outlined"
                                    color="primary"
                                    icon={<Bluetooth fontSize="small" />}
                                    onClick={() => handleConnectKnown(device)}
                                    sx={{ cursor: 'pointer', fontSize: '0.7rem' }}
                                  />
                                )}
                                <Tooltip title="Forget this printer">
                                  <IconButton size="small" onClick={() => handleForget(device.id)} sx={{ color: txt.muted }}>
                                    <Close fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </ListItemSecondaryAction>
                          </ListItem>
                        </motion.div>
                      );
                    })}
                  </List>
                )}

                {/* How-to hint */}
                <Box mt={2} p={1.5} sx={cardBox}>
                  <Typography variant="caption" color={txt.muted} sx={{ lineHeight: 1.7 }}>
                    💡 <strong style={{ color: txt.secondary }}>How it works:</strong>{' '}
                    Pair your thermal printer in{' '}
                    {isAndroid ? 'Android Settings → Bluetooth' : 'Windows Settings → Bluetooth & devices'},
                    then click{' '}
                    <strong style={{ color: accent }}>Scan for Printer</strong>.
                    Bills print instantly via ESC/POS — no drivers needed.
                  </Typography>
                </Box>
              </>
            )}
          </Box>
        )}

        {/* ──────────────────────────────────────────────────────────────── */}
        {/* TAB 1 — SYSTEM PRINT                                             */}
        {/* ──────────────────────────────────────────────────────────────── */}
        {tab === 1 && (
          <Box p={2}>
            {/* Platform banner */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, p: 2, mb: 2, ...accentCard }}>
              <Box sx={{ color: accent, mt: 0.25 }}>{platformIcon}</Box>
              <Box>
                <Typography fontWeight={700} fontSize="0.9rem" color={accent}>
                  {platformLabel} Print System
                </Typography>
                <Typography variant="caption" color={txt.secondary} sx={{ lineHeight: 1.6 }}>
                  {isAndroid || isCapacitor
                    ? 'Opens the Android Print dialog — shows all Bluetooth, network, and cloud printers configured in Android Settings.'
                    : 'Opens the Windows print dialog (like Ctrl+P) — shows ALL installed printers: USB thermal, Bluetooth, network, PDF, and more.'}
                </Typography>
              </Box>
            </Box>

            {/* Printer type chips */}
            <Typography
              variant="caption"
              sx={{ color: txt.muted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', mb: 1 }}
            >
              Supported Printer Types
            </Typography>
            <Box display="flex" flexWrap="wrap" gap={0.75} mb={2.5}>
              {[
                { icon: <Bluetooth fontSize="small" />, label: 'Bluetooth (OS-paired)' },
                { icon: <Wifi fontSize="small" />, label: 'Network / Wi-Fi' },
                { icon: <Computer fontSize="small" />, label: 'USB / Connected' },
                { icon: <ReceiptLong fontSize="small" />, label: 'PDF / Save as File' },
              ].map((item) => (
                <Chip
                  key={item.label}
                  icon={item.icon}
                  label={item.label}
                  size="small"
                  variant="outlined"
                  sx={{
                    color: txt.secondary,
                    borderColor: glass.border,
                    fontSize: '0.72rem',
                    backgroundColor: glass.surface,
                  }}
                />
              ))}
            </Box>

            <Divider sx={{ borderColor: glass.divider, mb: 2 }} />

            {/* Receipt preview */}
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
              <Typography fontSize="0.85rem" color={txt.secondary} fontWeight={600}>
                {bill ? `Bill: ${bill.billNumber}  ·  ₹${bill.total.toFixed(0)}` : 'No bill data'}
              </Typography>
              <Button
                size="small"
                variant="text"
                color="primary"
                startIcon={<ReceiptLong fontSize="small" />}
                onClick={() => setPreviewOpen((v) => !v)}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                {previewOpen ? 'Hide Preview' : 'Preview Receipt'}
              </Button>
            </Box>

            <AnimatePresence>
              {previewOpen && bill && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 280 }}
                  exit={{ opacity: 0, height: 0 }}
                  style={{ overflow: 'hidden', borderRadius: 12, marginBottom: 16 }}
                >
                  <iframe
                    title="Receipt Preview"
                    srcDoc={generateReceiptHTML(bill)}
                    style={{
                      width: '100%',
                      height: 280,
                      border: `1px solid ${glass.border}`,
                      borderRadius: 12,
                      background: '#fff',
                    }}
                  />
                </motion.div>
              )}
            </AnimatePresence>

            {/* Main print button */}
            <Button
              id="system-print-btn"
              fullWidth
              variant="contained"
              color="primary"
              size="large"
              startIcon={<Print />}
              onClick={handleSystemPrint}
              disabled={!bill?.items.length}
              sx={{
                borderRadius: '14px',
                textTransform: 'none',
                fontWeight: 800,
                fontSize: '1rem',
                py: 1.5,
                background: `linear-gradient(135deg, ${accent} 0%, ${food.redLight} 100%)`,
                boxShadow: `0 8px 24px ${accentGlow}`,
                '&:hover': {
                  background: `linear-gradient(135deg, ${food.redDark} 0%, ${accent} 100%)`,
                  boxShadow: `0 12px 28px ${accentGlow}`,
                  transform: 'translateY(-2px)',
                },
                transition: 'all 0.2s ease',
              }}
            >
              {isAndroid || isCapacitor ? 'Open Android Print Dialog' : 'Open Print Dialog (All Printers)'}
            </Button>

            <Typography
              variant="caption"
              sx={{ display: 'block', textAlign: 'center', color: txt.muted, mt: 1.5, lineHeight: 1.6 }}
            >
              The OS print dialog will open — select your printer from the list.
              {isAndroid
                ? ' Ensure your Bluetooth printer is paired in Android Settings.'
                : ' Bluetooth printers appear once installed via Windows Bluetooth & devices.'}
            </Typography>
          </Box>
        )}

        <Box pb={1} />
      </DialogContent>
    </Dialog>
  );
};

export default PrintManagerDialog;
